import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useShop, shopApi, INR } from '../lib/shop';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import PriceDetails from '../components/PriceDetails';
import '../styles/shop.css';

const CartItem = ({ it, onQty, onRemove }) => (
  <div className="p-4 md:p-5 flex gap-4 border-b border-[var(--si-line)] last:border-0" data-testid={`cart-item-${it.product_slug}`}>
    <div className="flex flex-col items-center gap-3">
      <Link to={`/shop/product/${it.product_slug}`} className="w-24 h-32 rounded overflow-hidden bg-[var(--si-bg-soft)] block">
        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
      </Link>
      <div className="flex items-center gap-2">
        <button onClick={() => onQty(it, -1)} disabled={it.qty <= 1} aria-label="Decrease quantity" data-testid={`cart-qty-minus-${it.product_slug}`}
          className="w-7 h-7 rounded-full border border-[#C7CCD6] flex items-center justify-center disabled:opacity-40"><Minus className="w-3.5 h-3.5" /></button>
        <span className="w-9 h-7 border border-[#C7CCD6] rounded flex items-center justify-center text-sm font-semibold" data-testid={`cart-qty-${it.product_slug}`}>{it.qty}</span>
        <button onClick={() => onQty(it, 1)} aria-label="Increase quantity" data-testid={`cart-qty-plus-${it.product_slug}`}
          className="w-7 h-7 rounded-full border border-[#C7CCD6] flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <Link to={`/shop/product/${it.product_slug}`} className="text-base font-medium text-[var(--si-black)] hover:text-[var(--si-blue)] line-clamp-2">{it.name}</Link>
      <p className="text-sm text-[var(--si-grey)] mt-1">{[it.size && `Size: ${it.size}`, it.color && `Colour: ${it.color}`].filter(Boolean).join(' · ')}</p>
      {it.offer && <span className="mt-1 inline-block bg-[var(--si-green)] text-white text-[11px] font-bold uppercase px-2 py-0.5 rounded-sm">{it.offer === 'b1g1' ? 'Buy 1 Get 1 Free' : 'Buy 2 Get 1 Free'}</span>}
      {it.in_stock === false && <p className="mt-1 text-sm font-bold text-[#B91C1C]" data-testid={`cart-oos-${it.product_slug}`}>Out of stock. Please remove this item to place your order.</p>}
      <p className="mt-3 flex items-baseline gap-2">
        <span className="text-lg font-bold" data-testid={`cart-line-total-${it.product_slug}`}>{INR(it.line_total)}</span>
        {it.qty > 1 && <span className="text-sm text-[var(--si-grey)]">({it.qty} × {INR(it.price)})</span>}
      </p>
      <button onClick={() => onRemove(it)} data-testid={`cart-remove-${it.product_slug}`}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold uppercase text-[var(--si-black)] hover:text-[#DC2626]">
        <Trash2 className="w-4 h-4" /> Remove
      </button>
    </div>
  </div>
);

const Cart = () => {
  const { cart, refreshCart } = useShop();
  const nav = useNavigate();
  const items = cart.items || [];
  const onQty = async (it, d) => { await shopApi.updateCartItem(it.product_slug, it.size, it.color, Math.max(1, it.qty + d)); refreshCart(); };
  const onRemove = async (it) => { await shopApi.removeFromCart(it.product_slug, it.size, it.color); refreshCart(); };

  return (
    <div className="si-page">
      <ShopNavbar />
      <div className="max-w-6xl mx-auto px-3 md:px-6 py-4 md:py-6 min-w-0">
        {items.length === 0 ? (
          <div className="si-card py-16 px-6 text-center" data-testid="cart-empty">
            <ShoppingCart className="w-16 h-16 mx-auto text-[var(--si-blue)]" />
            <p className="mt-4 text-xl font-bold">Your cart is empty</p>
            <p className="mt-1 text-[var(--si-grey)]">Add items to it now.</p>
            <Link to="/shop" className="mt-5 si-btn si-btn-primary" data-testid="cart-shop-now">Shop Now</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-4">
            <div className="si-card" data-testid="cart-items">
              <p className="px-5 py-3 border-b border-[var(--si-line)] text-lg font-bold">My Cart ({items.length})</p>
              {items.map((it) => <CartItem key={`${it.product_slug}-${it.size}-${it.color}`} it={it} onQty={onQty} onRemove={onRemove} />)}
            </div>
            <PriceDetails items={items} subtotal={cart.subtotal} offers={cart.offers || []}>
              <button onClick={() => nav('/checkout')} disabled={items.some((it) => it.in_stock === false)} className="si-btn si-btn-buy w-full !py-4 text-base" data-testid="cart-checkout-btn">PLACE ORDER</button>
            </PriceDetails>
          </div>
        )}
      </div>
      <ShopFooter />
    </div>
  );
};

export default Cart;
